/**
 * Full-page view for managing pending agent approval decisions.
 * Merges API query data with real-time WebSocket store data.
 * Uses infinite scroll pagination via useInfiniteQuery.
 * Bulk approve/reject uses dedicated backend endpoints.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle, Clock, XCircle } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { historyService } from '@/services/history';
import { agentService } from '@/services/agent';
import { type AgentDecision, useAgentStore } from '@/stores/agentStore';
import { useApprovalActions } from '@/hooks/useApprovalActions';
import { toast } from '@/hooks/useToast';

function DecisionCard({
  decision,
  onApprove,
  onReject,
  onQueue,
  isProcessing,
}: {
  decision: AgentDecision;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onQueue: (id: string) => void;
  isProcessing: boolean;
}) {
  const isBuy = decision.decisionType === 'buy';

  return (
    <Card className="border-l-4" style={{ borderLeftColor: isBuy ? '#22c55e' : '#ef4444' }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold">{decision.symbol}</span>
              <span
                className={cn(
                  'text-xs font-semibold px-2 py-0.5 rounded',
                  isBuy ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
                )}
              >
                {decision.decisionType.toUpperCase() ?? '—'}
              </span>
              <span className="text-sm text-muted-foreground">
                {decision.quantity} shares @ {decision.price ? formatCurrency(decision.price) : '-'}
              </span>
            </div>

            <p className="text-sm text-muted-foreground">{decision.reasoning}</p>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{Math.round(decision.confidence * 100)}% confidence</span>
              <span>{formatDate(decision.createdAt)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-red-200 hover:bg-red-50"
              onClick={() => onReject(decision.id)}
              disabled={isProcessing}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-primary border-blue-200 hover:bg-primary/10"
              onClick={() => onQueue(decision.id)}
              disabled={isProcessing}
            >
              <Clock className="h-4 w-4 mr-1" />
              Queue
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-green-700 text-white"
              onClick={() => onApprove(decision.id)}
              disabled={isProcessing}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonCards() {
  return (
    <div className="space-y-4" data-testid="approvals-skeleton">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="animate-pulse space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-5 w-16 bg-muted rounded" />
                <div className="h-5 w-10 bg-muted rounded" />
                <div className="h-4 w-32 bg-muted rounded" />
              </div>
              <div className="h-4 w-3/4 bg-muted rounded" />
              <div className="h-3 w-1/3 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Bell className="h-12 w-12 text-muted-foreground/40 mb-4" />
      <p className="text-lg font-medium text-muted-foreground">No pending approvals</p>
      <p className="text-sm text-muted-foreground/60">You're all caught up</p>
    </div>
  );
}

export function Approvals() {
  const { handleApprove, handleReject, handleQueue, isProcessing } = useApprovalActions();
  const storePending = useAgentStore((s) => s.pendingApprovals);
  const [bulkConfirm, setBulkConfirm] = useState<'approve' | 'reject' | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['agent', 'pending-approvals'],
    queryFn: ({ pageParam = 1 }) =>
      historyService.getAgentDecisions({
        outcome: 'awaiting_approval',
        page: pageParam,
        pageSize: 50,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) return lastPage.page + 1;
      return undefined;
    },
    initialPageParam: 1,
    refetchInterval: 15000,
  });

  // Flatten all pages into a single decisions array
  const apiDecisions = useMemo(
    () => data?.pages.flatMap((p) => p.decisions) ?? [],
    [data?.pages]
  );

  // Merge API + WebSocket store data, deduplicating by ID (store overrides API)
  const decisions = useMemo(() => {
    const storeIds = new Set(storePending.map((d) => d.id));
    const fromApi = apiDecisions.filter((d) => !storeIds.has(d.id));
    return [...storePending, ...fromApi];
  }, [apiDecisions, storePending]);

  // Total count from API response (first page has the total)
  const totalCount = data?.pages[0]?.total ?? decisions.length;

  // IntersectionObserver ref for infinite scroll auto-loading
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleBulkConfirm = async () => {
    try {
      if (bulkConfirm === 'approve') {
        const result = await agentService.bulkApproveAll();
        toast({ title: 'All Approved', description: result.message, variant: 'success' });
      } else {
        const result = await agentService.bulkRejectAll();
        toast({ title: 'All Rejected', description: result.message, variant: 'default' });
      }
      queryClient.invalidateQueries({ queryKey: ['agent', 'pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'decisions'] });
    } catch {
      toast({ title: 'Action Failed', description: 'Bulk action failed', variant: 'destructive' });
    }
    setBulkConfirm(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pending Approvals</h1>
          <p className="text-muted-foreground">
            Review and act on agent trading decisions
          </p>
        </div>

        {decisions.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-red-200 hover:bg-red-50"
              onClick={() => setBulkConfirm('reject')}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Reject All
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-green-700 text-white"
              onClick={() => setBulkConfirm('approve')}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve All
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <SkeletonCards />
      ) : decisions.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {decisions.map((decision, index) => {
            const isLast = index === decisions.length - 1;
            return (
              <div key={decision.id} ref={isLast ? lastCardRef : undefined}>
                <DecisionCard
                  decision={decision}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onQueue={handleQueue}
                  isProcessing={isProcessing(decision.id)}
                />
              </div>
            );
          })}

          {isFetchingNextPage && (
            <div className="flex justify-center py-4" data-testid="infinite-scroll-spinner">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          )}
        </div>
      )}

      <AlertDialog open={bulkConfirm !== null} onOpenChange={() => setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will {bulkConfirm === 'approve' ? 'approve' : 'reject'} all {totalCount} pending
              decisions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkConfirm}
              className={
                bulkConfirm === 'reject'
                  ? 'bg-destructive hover:bg-red-700'
                  : 'bg-primary hover:bg-green-700'
              }
            >
              Yes, {bulkConfirm === 'approve' ? 'approve' : 'reject'} all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Approvals;
